/**
 * buildStatementPlaceholders — pure Function, baut den flachen
 * Platzhalter-Record für die Stellungnahme-Generierung.
 *
 * Vertrag (siehe `docs/Platzhalter_Referenz.md`):
 *   - Alle ★-Platzhalter werden gesetzt (auch Pflicht).
 *   - ⚙-Platzhalter werden aus den Daten berechnet.
 *   - ◇-Platzhalter werden auf leeren String gesetzt, wenn Eingabe fehlt.
 *   - Output ist immer ein Record<string, string> — docxtemplater erwartet
 *     Strings.
 *
 * Diese Funktion ist deterministisch und seiteneffektfrei. Tests gegen
 * Beispiel-Cases prüfen die exakte Belegung jedes Platzhalters.
 */

import type { MoneyCents } from '../schema/brands.js';
import type { DimensionKey } from '../schema/dimensionData.js';
import { computeTragfaehigkeitsLuecke } from '../bg/computeTragfaehigkeitsLuecke.js';
import {
  describeBgComposition,
  formatEuro,
  formatEuroPerHour,
  formatEuroSigned,
  formatGermanDate,
  formatPercent,
  shortBgComposition,
} from './formatters.js';
import {
  RECOMMENDATION_SHORT_BY_TRAFFIC,
  TRAFFIC_LABEL_DE,
  VERDICT_BY_TRAFFIC,
  type BuildPlaceholdersInput,
  type StatementPlaceholders,
} from './types.js';

const DIMENSION_ORDER: ReadonlyArray<DimensionKey> = [
  'identity',
  'activity',
  'style',
  'resources',
  'health',
  'private',
];

function getDimensionTraffic(score: number): 'green' | 'yellow' | 'red' {
  if (score >= 70) return 'green';
  if (score >= 40) return 'yellow';
  return 'red';
}

function findDimensionScore(
  scores: BuildPlaceholdersInput['score']['dimensionScores'],
  key: DimensionKey,
): number | null {
  const entry = scores.find((s) => s.dimension === key);
  return entry ? entry.score : null;
}

function avgCents(values: ReadonlyArray<MoneyCents>): MoneyCents {
  if (values.length === 0) return 0 as MoneyCents;
  const sum = values.reduce<number>((s, v) => s + (v as number), 0);
  return Math.round(sum / values.length) as MoneyCents;
}

export function buildStatementPlaceholders(input: BuildPlaceholdersInput): StatementPlaceholders {
  const {
    caseData,
    client,
    business,
    score,
    financial,
    provider,
    recipient,
    meeting,
    assessments,
  } = input;

  // BWA-Aggregate
  const months = financial.monthlyAggregates;
  const avgRevenueCents = avgCents(months.map((m) => m.revenueCents));
  const avgCostsCents = avgCents(months.map((m) => m.costsCents));
  const avgProfitCents = ((avgRevenueCents as number) - (avgCostsCents as number)) as MoneyCents;

  // Stundensatz
  const hours = financial.hourlyRate.billableHoursPerMonth as number | undefined;
  const hourlyRateNet =
    typeof hours === 'number' && hours > 0 ? (avgProfitCents as number) / 100 / hours : 0;

  // Liquidität & Runway — echte Netto-Liquidität (abzügl. kurzfr. Verbindlichkeiten)
  // und echte Burn-Rate (Fallback: Ø-Betriebsausgaben), keine harten Defaults.
  const liquidAssets = (financial.liquidity?.liquidAssetsCents as number | undefined) ?? 0;
  const shortTermLiabilities =
    (financial.liquidity?.shortTermLiabilitiesCents as number | undefined) ?? 0;
  const liquidity = Math.max(0, liquidAssets - shortTermLiabilities) as MoneyCents;
  const burnRateReal = financial.monthlyBurnRateCents as number | undefined;
  const burnRate =
    typeof burnRateReal === 'number' && burnRateReal > 0
      ? burnRateReal
      : (avgCostsCents as number) > 0
        ? (avgCostsCents as number)
        : 1;
  const runwayWeeks =
    (liquidity as number) > 0 ? Math.round(((liquidity as number) / burnRate) * 4.33) : 0;
  const runwayDisplay =
    (avgProfitCents as number) > 0 ? 'unbegrenzt (positiver Cashflow)' : `${runwayWeeks} Wochen`;

  // Auslastung (vs. 160h/Monat = 100%)
  const utilizationPct = typeof hours === 'number' ? (hours / 160) * 100 : 0;

  // BG-Bedarf + § 11b-korrekte Tragfähigkeitslücke über den Core (Freibeträge!).
  // Bemessungsgrundlage: erfasstes Brutto-Erwerbseinkommen, sonst der Gewinn als
  // Näherung (für Selbständige = Betriebseinnahmen − Betriebsausgaben, § 3 Alg II-V);
  // plus sonstiges anrechenbares Einkommen (voll, ohne Freibetrag).
  const bruttoCents =
    (financial.bruttoErwerbseinkommenCents as number | undefined) ?? Math.max(0, avgProfitCents);
  const sonstigesEinkommenCents = (financial.sonstigesEinkommenCents as number | undefined) ?? 0;
  const lueckeResult = computeTragfaehigkeitsLuecke({
    bg: financial.bgComposition,
    bruttoErwerbseinkommenCents: Math.max(0, bruttoCents) as MoneyCents,
    sonstigesEinkommenCents: Math.max(0, sonstigesEinkommenCents) as MoneyCents,
  });
  const bgResult = lueckeResult.bedarf;
  const bgTotalNeed = bgResult.totalBedarfCents;
  const bgRegelbedarf = bgResult.totalRegelbedarfCents;
  const bgKdu =
    (financial.bgComposition.kdu.kaltmiete as number) +
    (financial.bgComposition.kdu.nebenkosten as number) +
    (financial.bgComposition.kdu.heizkosten as number);
  const bgOtherIncome = lueckeResult.einkommen.sonstigesEinkommenCents;
  const luecke = lueckeResult.luckeCents;
  const bgUncovered = Math.max(0, luecke) as MoneyCents;

  // Verdict
  const trafficKey = score.trafficLight;
  const verdict = VERDICT_BY_TRAFFIC[trafficKey];
  const recommendationShort = RECOMMENDATION_SHORT_BY_TRAFFIC[trafficKey];

  // Dimensions-Texte (DIM1..DIM6)
  const dimensionPlaceholders: Record<string, string> = {};
  DIMENSION_ORDER.forEach((key, idx) => {
    const n = idx + 1;
    const dimScore = findDimensionScore(score.dimensionScores, key);
    const dimTexts = assessments.dimensionTexts[key];
    if (dimScore !== null) {
      dimensionPlaceholders[`DIM${String(n)}_SCORE`] = String(dimScore);
      dimensionPlaceholders[`DIM${String(n)}_TRAFFIC`] =
        TRAFFIC_LABEL_DE[getDimensionTraffic(dimScore)];
    } else {
      dimensionPlaceholders[`DIM${String(n)}_SCORE`] = '–';
      dimensionPlaceholders[`DIM${String(n)}_TRAFFIC`] = '–';
    }
    dimensionPlaceholders[`DIM${String(n)}_SHORT`] = dimTexts.short;
    dimensionPlaceholders[`DIM${String(n)}_DETAIL`] = dimTexts.detail;
    dimensionPlaceholders[`DIM${String(n)}_DETAIL_CLIENT`] = dimTexts.detail;
  });

  // Konfidenz-Interpretation
  const confidence = score.totalConfidencePercent as number;
  const confidenceInterpretation =
    confidence >= 60
      ? 'Diese Datenbasis erlaubt eine fundierte Bewertung.'
      : 'Diese Datenbasis ist nicht abschließend; eine Nachforderung weiterer Unterlagen wird empfohlen.';

  // Case-ID kurz formatieren (TF-2026-NNNN)
  const caseShortId = `TF-${meeting.statementDate.getUTCFullYear()}-${String(caseData.id).slice(0, 8).toUpperCase()}`;

  return {
    // A · Stamm- und Kontextdaten
    CASE_ID: caseShortId,
    STATEMENT_DATE: formatGermanDate(meeting.statementDate),
    CLIENT_FULL_NAME: client.fullName,
    CLIENT_FIRST_NAME: client.firstName ?? client.fullName.split(' ')[0] ?? '',
    CLIENT_BIRTHDATE: client.birthDate ? formatGermanDate(client.birthDate) : '',
    CLIENT_ADDRESS: client.address ?? '',

    // B · Jobcenter-Daten (B2G)
    JC_NAME: recipient.jobcenterName ?? '',
    JC_ADDRESS_LINE1: recipient.jobcenterAddressLine1 ?? '',
    JC_ADDRESS_LINE2: recipient.jobcenterAddressLine2 ?? '',
    JC_CONTACT_NAME: recipient.jobcenterContact ?? '',
    JC_SALUTATION: recipient.jobcenterSalutation ?? '',
    JC_FILE_NUMBER: recipient.jobcenterFileNumber ?? '',
    ORDER_DATE: recipient.orderDate ? formatGermanDate(recipient.orderDate) : '',

    // C · Petschwork-Provider-Daten
    PROVIDER_NAME: provider.name,
    PROVIDER_ADDRESS_LINE1: provider.addressLine1,
    PROVIDER_ADDRESS_LINE2: provider.addressLine2,
    PROVIDER_PHONE_EMAIL: provider.phoneEmail,
    PROVIDER_CERTIFICATIONS: provider.certifications,
    COACH_NAME: provider.coachName,

    // D · Termin- und Verfahrensdaten
    // Optionale Werte: leerer String, wenn nicht erfasst — renderHtml weist
    // das als „nicht erfasst" aus statt einen Termin zu erfinden.
    DATA_SUBMISSION_DATE: meeting.dataSubmissionDate
      ? formatGermanDate(meeting.dataSubmissionDate)
      : '',
    MEETING_DATE: meeting.meetingDate ? formatGermanDate(meeting.meetingDate) : '',
    MEETING_DURATION_MIN:
      typeof meeting.meetingDurationMinutes === 'number'
        ? String(meeting.meetingDurationMinutes)
        : '',
    MEETING_FORMAT: meeting.meetingFormat ?? '',
    DOCUMENT_LIST_BWA: meeting.documentListBwa,
    DOCUMENT_LIST_EKS: meeting.documentListEks,
    DOCUMENT_LIST_ADDITIONAL: meeting.documentListAdditional ?? '',

    // E · Geschäftsdaten
    BUSINESS_BRANCH: business.branch,
    BUSINESS_LEGAL_FORM: business.legalForm,
    BUSINESS_START_DATE: business.startDate ? formatGermanDate(business.startDate) : '',
    BUSINESS_TAX_ID: business.taxId,
    BUSINESS_DESCRIPTION: business.description,

    // F · Bedarfsgemeinschaft
    BG_DESCRIPTION: describeBgComposition(financial.bgComposition.members),
    BG_COMPOSITION: shortBgComposition(financial.bgComposition.members.length),
    BENEFIT_START_DATE: meeting.benefitStartDate ? formatGermanDate(meeting.benefitStartDate) : '',
    BG_REGELBEDARF: formatEuro(bgRegelbedarf),
    BG_KDU: formatEuro(bgKdu as MoneyCents),
    BG_OTHER_INCOME: formatEuro(bgOtherIncome),
    BG_TOTAL_NEED: formatEuro(bgTotalNeed),
    BG_UNCOVERED: formatEuro(bgUncovered),
    TRAGFAEHIGKEITSLUECKE: formatEuroSigned(luecke),

    // G · Wirtschaftliche Kennzahlen
    REVENUE_MONTHLY: formatEuro(avgRevenueCents),
    COSTS_MONTHLY: formatEuro(avgCostsCents),
    PROFIT_MONTHLY: formatEuro(avgProfitCents),
    HOURLY_RATE_NET: formatEuroPerHour(hourlyRateNet),
    LIQUIDITY: formatEuro(liquidity),
    RUNWAY_WEEKS: runwayDisplay,
    UTILIZATION_PCT: formatPercent(utilizationPct),

    // H · Bewertungs-Texte
    REVENUE_ASSESSMENT: assessments.revenueAssessment ?? '',
    COSTS_ASSESSMENT: assessments.costsAssessment ?? '',
    PROFIT_ASSESSMENT: assessments.profitAssessment ?? '',
    HOURLY_RATE_ASSESSMENT: assessments.hourlyRateAssessment ?? '',
    LIQUIDITY_ASSESSMENT: assessments.liquidityAssessment ?? '',
    RUNWAY_ASSESSMENT: assessments.runwayAssessment ?? '',
    UTILIZATION_ASSESSMENT: assessments.utilizationAssessment ?? '',
    GAP_INTERPRETATION:
      assessments.gapInterpretation ??
      ((luecke as number) > 0
        ? 'nicht in der Lage ist'
        : (luecke as number) < 0
          ? 'in der Lage ist'
          : 'gerade in der Lage ist'),

    // I · Score & Verdict
    SCORE_VALUE: String(score.totalScore),
    SCORE_CONFIDENCE: String(confidence),
    VERDICT: verdict,
    RECOMMENDATION_SHORT: recommendationShort,
    CONFIDENCE_INTERPRETATION: confidenceInterpretation,

    // J · Sechs Dimensionen
    ...dimensionPlaceholders,

    // K · Risiken / Stärken / Empfehlungen
    RISK_INDICATORS: assessments.riskIndicators,
    RISK_INDICATORS_CLIENT: assessments.riskIndicators,
    STRENGTH_INDICATORS: assessments.strengthIndicators,
    STRENGTH_INDICATORS_CLIENT: assessments.strengthIndicators,
    CONSTRAINT_DISCUSSION: assessments.constraintDiscussion,
    CONSTRAINT_DISCUSSION_CLIENT: assessments.constraintDiscussion,
    VERDICT_LONG_PARAGRAPH: assessments.verdictLongParagraph,
    VERDICT_REASONING: assessments.verdictReasoning,
    RECOMMENDATION_DETAILED: assessments.recommendationDetailed,
    CONDITIONS_PARAGRAPH: assessments.conditionsParagraph ?? '',
    REEVALUATION_PROPOSAL: assessments.reevaluationProposal ?? '',
    VERDICT_EXPLANATION_FOR_CLIENT: assessments.verdictLongParagraph,
  };
}

// Re-Export der Eingabe-Typen für Aufrufer in apps/web
export type {
  BuildPlaceholdersInput,
  StatementPlaceholders,
  ProviderProfile,
  RecipientInfo,
  ClientStaticData,
  BusinessProfile,
  MeetingMetadata,
  AssessmentTexts,
} from './types.js';
